import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { PromoCmsComponents } from '@core/const';
import { CmsFormattedResponsiveBannerComponent } from '@promo/const';
import { DeviceService } from '@shared/services';
import { CmsService, ContentSlotComponentData, CmsBannerCarouselComponent as model } from '@spartacus/core';
import { BREAKPOINT, CmsComponentData } from '@spartacus/storefront';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SwiperOptions } from 'swiper/types';
import { SdBannerCarouselComponent } from '../banner-carousel/banner-carousel.component';

const ROWS_PER_SLIDE = 2;

@Component({
  selector: 'sd-banner-carousel-two-rows',
  templateUrl: 'banner-carousel-two-rows.component.html',
  styleUrls: ['./banner-carousel-two-rows.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdBannerCarouselTwoRowsComponent extends SdBannerCarouselComponent implements OnInit {
  defaultConfig: SwiperOptions = {
    slidesPerView: 1,
  };

  groupedBanners$: Observable<CmsFormattedResponsiveBannerComponent[][]>;
  private responsiveBanners$: Observable<CmsFormattedResponsiveBannerComponent[]>;

  constructor(
    private deviceService: DeviceService,
    componentData: CmsComponentData<model>,
    cmsService: CmsService,
  ) {
    super(componentData, cmsService);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.responsiveBanners$ = this.items$.pipe(
      map(
        (banners) =>
          banners.filter((banner) =>
            this.isFormattedResponsiveBannerComponent(banner),
          ) as CmsFormattedResponsiveBannerComponent[], // в 5 версии тайпскрипта не требуется кастить
      ),
    );

    this.groupedBanners$ = this.deviceService.breakpoint$.pipe(
      switchMap((breakpoint) => this.responsiveBanners$.pipe(map((banners) => this.groupBanners(banners, breakpoint)))),
    );
  }

  private groupBanners(
    banners: CmsFormattedResponsiveBannerComponent[],
    breakpoint: BREAKPOINT,
  ): CmsFormattedResponsiveBannerComponent[][] {
    const bannersPerSlide: CmsFormattedResponsiveBannerComponent[][] = [];
    let currentSlide: CmsFormattedResponsiveBannerComponent[] = [];
    let currentCols = 0;

    banners.forEach((banner) => {
      const cols = this.extractCols(banner, breakpoint);

      if (currentCols + cols > 12 * ROWS_PER_SLIDE) {
        bannersPerSlide.push(currentSlide);
        currentSlide = [];
        currentCols = 0;
      }

      currentSlide.push(banner);
      currentCols += cols;
    });

    // добавляем последний слайд, который может быть не полным
    if (currentSlide.length > 0) {
      bannersPerSlide.push(currentSlide);
    }

    return bannersPerSlide;
  }

  private extractCols(banner: CmsFormattedResponsiveBannerComponent, breakpoint: BREAKPOINT): number {
    // не все форматы банеров могут быть указаны, поэтому используем маппинг
    // prettier-ignore
    const formatMapping = {
      [BREAKPOINT.xs]: banner.mobileBannerFormat || banner.tabletBannerFormat || banner.desktopBannerFormat || banner.widescreenBannerFormat,
      [BREAKPOINT.sm]: banner.mobileBannerFormat || banner.tabletBannerFormat || banner.desktopBannerFormat || banner.widescreenBannerFormat,
      [BREAKPOINT.md]: banner.tabletBannerFormat || banner.mobileBannerFormat || banner.desktopBannerFormat || banner.widescreenBannerFormat,
      [BREAKPOINT.lg]: banner.desktopBannerFormat || banner.tabletBannerFormat || banner.mobileBannerFormat || banner.widescreenBannerFormat,
      [BREAKPOINT.xl]: banner.widescreenBannerFormat || banner.desktopBannerFormat || banner.tabletBannerFormat || banner.mobileBannerFormat,
      [BREAKPOINT.xxl]: banner.widescreenBannerFormat || banner.desktopBannerFormat || banner.tabletBannerFormat || banner.mobileBannerFormat,
    };

    const format = formatMapping[breakpoint];
    if (format) {
      const cols = parseInt(format.substring(1), 10);
      if (!isNaN(cols)) {
        return cols;
      }
    }

    return 12; // если формат не найден
  }

  private isFormattedResponsiveBannerComponent(
    banner: ContentSlotComponentData,
  ): banner is CmsFormattedResponsiveBannerComponent {
    return (
      (banner as CmsFormattedResponsiveBannerComponent).typeCode ===
      PromoCmsComponents.RosaFormattedResponsiveBannerComponent
    );
  }
}
